import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { IState } from 'models/reducers/i-state.interface';

import {
  clearStoredNetworkOverride,
  getActiveNetworkOverride,
  getStoredNetworkOverride,
  MAINNET_CONFIG,
  RuntimeNetworkId,
  setStoredNetworkOverride,
  TESTNET_CONFIG,
} from 'constants/runtime-network';

import { setWalletAddress } from 'actions/wallet.action';
import { setIsConnected, setIsOnCorrectChain } from 'actions/web3.action';

import { DEV_IMPERSONATION_STORAGE_KEY, isDevImpersonationActive } from 'utils/dev-impersonation.utils';

const IS_DEV = process.env.NODE_ENV === 'development';
const IMPERSONATION_STORAGE_KEY = DEV_IMPERSONATION_STORAGE_KEY;
const ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
const ALLOWED_PATH = '/staking';

const NETWORK_OPTIONS: { id: RuntimeNetworkId; label: string; networkId: string }[] = [
  { id: 'mainnet', label: 'Mainnet', networkId: MAINNET_CONFIG.networkId },
  { id: 'testnet', label: 'Testnet', networkId: TESTNET_CONFIG.networkId },
];

const readStoredImpersonation = (): string => {
  if (!IS_DEV || typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const handleStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '2px 10px',
  background: '#1a1f3d',
  borderBottom: '1px solid #2D5EFF',
  color: '#9bb0ff',
  fontFamily: 'monospace',
  fontSize: 10,
  cursor: 'pointer',
  letterSpacing: 1,
  userSelect: 'none',
};

const handleActiveDotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#F83A56',
};

const containerStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '6px 12px',
  background: '#1a1f3d',
  borderBottom: '1px solid #2D5EFF',
  color: '#fff',
  fontFamily: 'monospace',
  fontSize: 12,
};

const tagStyle: CSSProperties = {
  background: '#F83A56',
  color: '#fff',
  padding: '2px 8px',
  borderRadius: 4,
  fontWeight: 700,
  letterSpacing: 0.6,
};

const readOnlyBadgeStyle: CSSProperties = {
  background: '#FFD000',
  color: '#1a1f3d',
  padding: '2px 8px',
  borderRadius: 4,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
};

const chainGuardBadgeStyle: CSSProperties = {
  background: '#F83A56',
  color: '#fff',
  padding: '2px 8px',
  borderRadius: 4,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  fontSize: 11,
};

const pendingBadgeStyle: CSSProperties = {
  background: '#FFD000',
  color: '#1a1f3d',
  padding: '2px 8px',
  borderRadius: 4,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  fontSize: 11,
};

const pillGroupStyle: CSSProperties = {
  display: 'flex',
  gap: 4,
  background: '#0e1226',
  padding: 2,
  borderRadius: 999,
};

const basePillStyle: CSSProperties = {
  border: 'none',
  borderRadius: 999,
  padding: '3px 10px',
  fontFamily: 'inherit',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  cursor: 'pointer',
};

const inputStyle: CSSProperties = {
  flex: '1 1 360px',
  minWidth: 280,
  background: '#0e1226',
  border: '1px solid #2D5EFF',
  borderRadius: 4,
  color: '#fff',
  padding: '4px 8px',
  fontFamily: 'inherit',
  fontSize: 12,
};

const baseButtonStyle: CSSProperties = {
  border: 'none',
  borderRadius: 4,
  padding: '4px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
};

const applyButtonStyle: CSSProperties = {
  ...baseButtonStyle,
  background: '#2D5EFF',
  color: '#fff',
};

const applyDisabledStyle: CSSProperties = {
  ...applyButtonStyle,
  background: '#2a2f4d',
  color: '#6b7bbf',
  cursor: 'not-allowed',
};

const clearButtonStyle: CSSProperties = {
  ...baseButtonStyle,
  background: 'transparent',
  border: '1px solid #435186',
  color: '#9bb0ff',
};

const statusStyle: CSSProperties = {
  color: '#9bb0ff',
  marginLeft: 'auto',
};

const closeButtonStyle: CSSProperties = {
  ...baseButtonStyle,
  background: 'transparent',
  border: '1px solid #F83A56',
  color: '#F83A56',
  padding: '2px 8px',
  fontSize: 14,
  lineHeight: 1,
  fontWeight: 700,
};

const detailsStyle: CSSProperties = {
  flexBasis: '100%',
  color: '#6b7bbf',
  fontSize: 11,
  paddingTop: 4,
  borderTop: '1px dashed #232a4d',
  marginTop: 2,
  wordBreak: 'break-all',
};

export const DevDebugHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentWallet = useSelector((state: IState) => state.wallet.address);

  // Drawer is collapsed by default. The user "pulls down" the handle to expand
  // it, edits any number of pending values, then clicks Apply to commit.
  const [isExpanded, setIsExpanded] = useState(false);

  // Pending impersonation address (text input). Empty string = "clear it on Apply".
  const [input, setInput] = useState('');

  // Only render on the staking page.
  const normalizedPath = location.pathname.replace(/\/+$/, '');
  const isOnAllowedPath = normalizedPath === ALLOWED_PATH;

  // What is actually stored right now (these don't update across re-renders
  // unless we reload — they are the "committed" state).
  const storedOverride = IS_DEV ? getStoredNetworkOverride() : null;
  const storedImpersonation = readStoredImpersonation();

  const envNetworkId = process.env.REACT_APP_NETWORK_ID || '';
  const activeEnvId: RuntimeNetworkId | null =
    envNetworkId === MAINNET_CONFIG.networkId
      ? 'mainnet'
      : envNetworkId === TESTNET_CONFIG.networkId
        ? 'testnet'
        : null;

  // Pending network selection. Starts at the currently stored override (or env
  // default if none). Updated by clicking pills — not committed until Apply.
  const [pendingNetwork, setPendingNetwork] = useState<RuntimeNetworkId | null>(
    () => storedOverride ?? activeEnvId
  );

  // What is actually loaded in this tab right now.
  const activeOverrideConfig = IS_DEV ? getActiveNetworkOverride() : null;
  const activeRpcUrl =
    activeOverrideConfig?.rpcUrl ??
    (() => {
      try {
        const parsed = JSON.parse(process.env.REACT_APP_ADD_NETWORK_SETTINGS || '{}');
        return parsed?.rpcUrls?.[0] ?? '';
      } catch {
        return '';
      }
    })();
  const activeVipAddress =
    activeOverrideConfig?.contracts.VIP_STAKE ?? process.env.REACT_APP_VIP_STAKE_ADDRESS ?? '';

  const applyImpersonation = useCallback(
    (address: string) => {
      dispatch(setWalletAddress(address));
      dispatch(setIsConnected(true));
      dispatch(setIsOnCorrectChain(true));
    },
    [dispatch]
  );

  // Restore impersonation on first mount AND pre-fill the input with the
  // currently active impersonation so the user sees the live state when they
  // pull the drawer down.
  useEffect(() => {
    if (!IS_DEV) return;
    const stored = readStoredImpersonation();
    if (stored && ADDRESS_REGEX.test(stored)) {
      setInput(stored);
      applyImpersonation(stored);
    }
  }, [applyImpersonation]);

  // Keep the textbox in sync if the wallet changes elsewhere (e.g. real connect)
  useEffect(() => {
    if (currentWallet && currentWallet !== input) {
      setInput(currentWallet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWallet]);

  // "Effective" pending override — pendingNetwork matching env default means
  // "drop the override" on Apply.
  const effectivePendingOverride: RuntimeNetworkId | null =
    pendingNetwork === activeEnvId ? null : pendingNetwork;

  const trimmedInput = input.trim();
  const isInputValidOrEmpty = trimmedInput === '' || ADDRESS_REGEX.test(trimmedInput);

  const isDirty =
    trimmedInput !== storedImpersonation || effectivePendingOverride !== storedOverride;

  const handleApply = useCallback(() => {
    if (trimmedInput !== '' && !ADDRESS_REGEX.test(trimmedInput)) {
      // eslint-disable-next-line no-alert
      window.alert('Invalid wallet address. Expected 0x + 40 hex chars.');
      return;
    }

    // Commit impersonation.
    if (trimmedInput === '') {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } else {
      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, trimmedInput);
    }

    // Commit network override.
    if (effectivePendingOverride === null) {
      clearStoredNetworkOverride();
    } else {
      setStoredNetworkOverride(effectivePendingOverride);
    }

    // Full reload — module-init state (NETWORK_SETTINGS, web3 singleton) is
    // captured once and won't pick up pending changes otherwise.
    window.location.reload();
  }, [trimmedInput, effectivePendingOverride]);

  const handleClearPending = useCallback(() => {
    setInput('');
    setPendingNetwork(activeEnvId);
  }, [activeEnvId]);

  const handleGoToStaking = useCallback(() => {
    navigate('/staking#tab=earnings&filter=all');
  }, [navigate]);

  if (!IS_DEV) return null;
  if (!isOnAllowedPath) return null;

  const isImpersonating = isDevImpersonationActive();
  const hasActiveDevState = isImpersonating || storedOverride !== null;

  // Collapsed: only a thin "pull-down" handle. Indicator dot when any dev
  // override is live so the dev can't miss it.
  if (!isExpanded) {
    return (
      <div
        style={handleStyle}
        onClick={() => setIsExpanded(true)}
        data-testid="dev-debug-handle"
        role="button"
        aria-label="Open dev debugger"
        title="Open dev debugger"
      >
        {hasActiveDevState && <span style={handleActiveDotStyle} />}
        <span>▾ DEV</span>
        {isImpersonating && <span>· IMPERSONATING</span>}
        {storedOverride && <span>· {storedOverride.toUpperCase()}</span>}
      </div>
    );
  }

  return (
    <div style={containerStyle} data-testid="dev-debug-header">
      <span style={tagStyle}>DEV</span>
      {isImpersonating && (
        <span style={readOnlyBadgeStyle} data-testid="dev-readonly-badge">
          READ-ONLY (impersonating)
        </span>
      )}
      {isDirty && <span style={pendingBadgeStyle}>Unapplied changes</span>}

      <div style={pillGroupStyle}>
        {NETWORK_OPTIONS.map((network) => {
          const isPendingActive = network.id === pendingNetwork;
          const isStoredActive = network.id === (storedOverride ?? activeEnvId);
          return (
            <button
              key={network.id}
              type="button"
              onClick={() => setPendingNetwork(network.id)}
              style={{
                ...basePillStyle,
                background: isPendingActive ? '#2D5EFF' : 'transparent',
                color: isPendingActive ? '#fff' : '#9bb0ff',
                outline: isPendingActive && !isStoredActive ? '1px dashed #FFD000' : 'none',
                outlineOffset: 2,
              }}
              title={
                isStoredActive
                  ? `Currently loaded — chain ${network.networkId}`
                  : `Stage ${network.label} (chain ${network.networkId}) — click Apply to reload`
              }
            >
              {network.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleGoToStaking}
        style={{ ...baseButtonStyle, background: '#20AA67', color: '#fff' }}
        title="Open /staking on the Earnings tab"
      >
        Open Staking
      </button>

      <input
        type="text"
        spellCheck={false}
        placeholder="Impersonate wallet (0x...) — leave empty to clear"
        value={input}
        onChange={(event) => setInput(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && isDirty && isInputValidOrEmpty) handleApply();
        }}
        style={inputStyle}
      />
      <button
        type="button"
        onClick={handleApply}
        style={isDirty && isInputValidOrEmpty ? applyButtonStyle : applyDisabledStyle}
        disabled={!isDirty || !isInputValidOrEmpty}
        title={
          !isDirty
            ? 'No pending changes'
            : !isInputValidOrEmpty
              ? 'Invalid wallet address'
              : 'Commit pending changes and reload'
        }
      >
        Apply
      </button>
      <button
        type="button"
        onClick={handleClearPending}
        style={clearButtonStyle}
        title="Reset pending changes to no-overrides (click Apply to commit)"
      >
        Clear
      </button>
      {isImpersonating && (
        <span style={chainGuardBadgeStyle} data-testid="dev-chain-guard-badge">
          Chain guards disabled
        </span>
      )}

      <span style={statusStyle}>
        {currentWallet ? `Viewing as ${currentWallet.slice(0, 6)}…${currentWallet.slice(-4)}` : 'No wallet'}
      </span>

      <button
        type="button"
        onClick={() => setIsExpanded(false)}
        style={closeButtonStyle}
        title="Collapse drawer (does not change applied state)"
        aria-label="Collapse dev debugger"
      >
        ×
      </button>

      <div style={detailsStyle}>
        {storedOverride ? `override=${storedOverride}` : 'override=none'} · RPC: {activeRpcUrl || '(unset)'} · VIP:{' '}
        {activeVipAddress || '(unset)'}
      </div>
    </div>
  );
};

export default DevDebugHeader;
